import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type BisProvider } from '@/scripts/core/global'

type RestockRocketSellingPlan = {
	shopify_selling_plan_id?: number
	shipping_text?: string
	variant_ids?: number[]
}

type RestockRocketProductVariant = {
	id?: number | string
	available?: boolean
}

type SyncSellingPlanInputArgs = {
	form: HTMLFormElement
	variantId?: string | number | null
	preorder: boolean
	liquidSellingPlanId?: string | number | null
}

type SyncShippingTextInputArgs = {
	form: HTMLFormElement
	preorder: boolean
	shippingText?: string | null
}

type ApplyPreorderPropertiesArgs = {
	formData: FormData
	preorder: boolean
	shippingText?: string | null
}

export type InventoryStatus = 'InStock' | 'PreOrder' | 'OutOfStock' | 'ComingSoon'

type ResolveVariantStateArgs = {
	variantId?: string | number | null
	available: boolean
	preorder: boolean
	inventoryStatus?: string | null
	inventoryStatusOverride?: string | null
	inventoryStatusText: string
	liquidSellingPlanId?: string | number | null
}

type ResolvedVariantState = {
	inventoryStatus: InventoryStatus
	available: boolean
	preorder: boolean
	inventoryStatusText: string
	sellingPlanId: string
	shippingText: string
}

type SyncVariantSelectionArgs = {
	form: HTMLFormElement
	submitButton: HTMLButtonElement
	variantId?: string | number | null
	resolvedState: ResolvedVariantState
	fallbackShippingText?: string | null
	oosPopupUrl?: string
	bisProvider?: BisProvider
	renderButton?: boolean
}

type ScheduledRestockRocketRender = {
	observer?: MutationObserver
	frame?: number
	innerFrame?: number
	timeout?: number
}

const SELECTORS = {
	sellingPlanInput: '[data-uc-selling-plan-id]',
	shippingTextInput: '[data-uc-stoq-shipping-text]',
}

const PROPERTIES = {
	displayPreorder: 'PREORDER',
	preorder: '_preorder',
	shippingText: '_stoq_shipping_text',
}

const MAX_RESTOCK_ROCKET_RENDER_WAIT_MS = 300

let scheduledRestockRocketRender: ScheduledRestockRocketRender | null = null

function normalizeInventoryStatus(
	value?: string | null
): InventoryStatus | '' {
	if (value === 'InStock' || value === 'PreOrder' || value === 'OutOfStock' || value === 'ComingSoon') {
		return value
	}

	return ''
}

function isPurchasableInventoryStatus(status: InventoryStatus): boolean {
	return status === 'InStock' || status === 'PreOrder'
}

function deriveLiquidInventoryStatus({
	available,
	preorder,
	inventoryStatus,
}: Pick<
	ResolveVariantStateArgs,
	'available' | 'preorder' | 'inventoryStatus'
>): InventoryStatus {
	const normalizedInventoryStatus = normalizeInventoryStatus(inventoryStatus)
	if (normalizedInventoryStatus !== '') return normalizedInventoryStatus
	if (preorder) return 'PreOrder'
	return available ? 'InStock' : 'OutOfStock'
}

function normalizeSellingPlanId(value?: string | number | null): string {
	if (typeof value === 'number') {
		return Number.isFinite(value) && value > 0 ? String(value) : ''
	}

	if (typeof value !== 'string') return ''

	const normalized = value.trim()
	return /^\d+$/.test(normalized) ? normalized : ''
}

function getRestockRocketSellingPlan(
	variantId: number
): RestockRocketSellingPlan | null {
	return window._RestockRocket?.getSellingPlan?.(variantId) ?? null
}

function normalizeOptionalText(value?: string | null): string {
	if (typeof value !== 'string') return ''
	return value.trim()
}

function getRestockRocketSellingPlanForVariant(
	variantId?: string | number | null
): RestockRocketSellingPlan | null {
	const numericVariantId = Number(variantId)
	if (!Number.isFinite(numericVariantId)) return null

	const sellingPlan = getRestockRocketSellingPlan(numericVariantId)
	if (!sellingPlan) return null

	const variantIds = Array.isArray(sellingPlan.variant_ids) ? sellingPlan.variant_ids : []
	if (variantIds.length > 0 && !variantIds.includes(numericVariantId)) return null

	return sellingPlan
}

function getRestockRocketSellingPlanIdForVariant(
	variantId?: string | number | null
): string {
	const sellingPlan = getRestockRocketSellingPlanForVariant(variantId)
	if (!sellingPlan) return ''

	return normalizeSellingPlanId(sellingPlan.shopify_selling_plan_id)
}

function getRestockRocketShippingTextForVariant(
	variantId?: string | number | null
): string {
	return normalizeOptionalText(
		getRestockRocketSellingPlanForVariant(variantId)?.shipping_text
	)
}

function getRestockRocketInventoryPolicyForVariant(
	numericVariantId: number
): string {
	return normalizeOptionalText(
		window._RestockRocketConfig?.variantsInventoryPolicy?.[String(numericVariantId)]
	)
}

function getRestockRocketInventoryQuantityForVariant(
	numericVariantId: number
): number | null {
	const quantity = window._RestockRocketConfig?.variantsInventoryQuantity?.[
		String(numericVariantId)
	]
	return typeof quantity === 'number' && Number.isFinite(quantity) ? quantity : null
}

function findRestockRocketProductVariant(
	numericVariantId: number
): RestockRocketProductVariant | null {
	const variants = window._RestockRocketConfig?.product?.variants
	if (!Array.isArray(variants)) return null

	return variants.find((variant) => Number(variant.id) === numericVariantId) ?? null
}

function syncRestockRocketConfigForVariant(
	numericVariantId: number,
	available?: boolean
) {
	const config = window._RestockRocketConfig
	if (!config) return

	config.selected_variant_id = numericVariantId

	const configVariant = findRestockRocketProductVariant(numericVariantId)
	if (typeof available === 'boolean') {
		config.selected_variant_available = available
		return
	}

	if (typeof configVariant?.available === 'boolean') {
		config.selected_variant_available = configVariant.available
	}
}

function isRestockRocketProductPage(): boolean {
	return window._RestockRocketConfig?.pageType === 'product'
}

function cleanupScheduledRestockRocketRender() {
	scheduledRestockRocketRender?.observer?.disconnect()

	if (typeof scheduledRestockRocketRender?.frame === 'number') {
		cancelAnimationFrame(scheduledRestockRocketRender.frame)
	}

	if (typeof scheduledRestockRocketRender?.innerFrame === 'number') {
		cancelAnimationFrame(scheduledRestockRocketRender.innerFrame)
	}

	if (typeof scheduledRestockRocketRender?.timeout === 'number') {
		clearTimeout(scheduledRestockRocketRender.timeout)
	}

	scheduledRestockRocketRender = null
}

function renderRestockRocketButtons(numericVariantId: number) {
	if (!isRestockRocketProductPage()) return

	window._RestockRocket?.renderButtonForVariant?.(numericVariantId)
	if (window._RestockRocketConfig) {
		window._RestockRocket?.renderPreorderButton?.(window._RestockRocketConfig.settings)
	}
}

function scheduleRestockRocketButtonRender(
	numericVariantId: number,
	root: HTMLElement | null
) {
	cleanupScheduledRestockRocketRender()

	const scheduledRender: ScheduledRestockRocketRender = {}
	scheduledRestockRocketRender = scheduledRender

	const render = () => {
		if (scheduledRestockRocketRender !== scheduledRender) return
		cleanupScheduledRestockRocketRender()
		renderRestockRocketButtons(numericVariantId)
	}

	const scheduleNextPaintRender = () => {
		if (scheduledRestockRocketRender !== scheduledRender) return

		if (typeof scheduledRender.frame === 'number') {
			cancelAnimationFrame(scheduledRender.frame)
		}
		if (typeof scheduledRender.innerFrame === 'number') {
			cancelAnimationFrame(scheduledRender.innerFrame)
		}

		scheduledRender.frame = requestAnimationFrame(() => {
			scheduledRender.innerFrame = requestAnimationFrame(render)
		})
	}

	if (typeof MutationObserver !== 'undefined' && root) {
		scheduledRender.observer = new MutationObserver(scheduleNextPaintRender)
		scheduledRender.observer.observe(root, {
			childList: true,
			subtree: true,
		})
	}

	scheduledRender.timeout = window.setTimeout(
		render,
		MAX_RESTOCK_ROCKET_RENDER_WAIT_MS
	)
	scheduleNextPaintRender()
}

// Returns true only when Stoq exposes a finite remaining-preorder count <= 0.
// Undefined / non-finite means "no cap configured" — leave the preorder open.
function isStoqPreorderCapReached(variantId?: string | number | null): boolean {
	const numericVariantId = Number(variantId)
	if (!Number.isFinite(numericVariantId)) return false

	const available = window._RestockRocket?.getPreorderAvailable?.(numericVariantId)
	if (typeof available !== 'number' || !Number.isFinite(available)) return false

	return available <= 0
}

function setLineItemProperty(formData: FormData, propertyName: string, value: string) {
	const propertyKey = formData.get('items[0][id]')
		? `items[0][properties[${propertyName}]]`
		: propertyName

	if (value) {
		formData.set(propertyKey, value)
		return
	}

	formData.delete(propertyKey)
}

export function syncStoqSellingPlanInput({
	form,
	variantId,
	preorder,
	liquidSellingPlanId,
}: SyncSellingPlanInputArgs) {
	const sellingPlanInput = q.os<HTMLInputElement>(SELECTORS.sellingPlanInput, form)
	if (!sellingPlanInput) return
	const existingSellingPlanId = normalizeSellingPlanId(sellingPlanInput.value)

	if (!preorder) {
		sellingPlanInput.value = ''
		sellingPlanInput.disabled = true
		return
	}

	const sellingPlanId =
		normalizeSellingPlanId(liquidSellingPlanId) ||
		getRestockRocketSellingPlanIdForVariant(variantId) ||
		existingSellingPlanId

	sellingPlanInput.value = sellingPlanId
	sellingPlanInput.disabled = sellingPlanId === ''
}

export function syncStoqShippingTextInput({
	form,
	preorder,
	shippingText,
}: SyncShippingTextInputArgs) {
	const shippingTextInput = q.os<HTMLInputElement>(SELECTORS.shippingTextInput, form)
	if (!shippingTextInput) return

	shippingTextInput.value = preorder ? normalizeOptionalText(shippingText) : ''
}

export function getStoqShippingTextInputValue(form: HTMLFormElement): string {
	return normalizeOptionalText(
		q.os<HTMLInputElement>(SELECTORS.shippingTextInput, form)?.value
	)
}

export function renderStoqButtonForVariant(
	variantId?: string | number | null,
	available?: boolean,
	root?: HTMLElement | null
) {
	const normalizedVariantId = Number(variantId)
	if (!Number.isFinite(normalizedVariantId)) return
	if (!isRestockRocketProductPage()) return

	syncRestockRocketConfigForVariant(normalizedVariantId, available)
	scheduleRestockRocketButtonRender(normalizedVariantId, root ?? null)
}

export function resolveStoqVariantState({
	variantId,
	available,
	preorder,
	inventoryStatus,
	inventoryStatusOverride,
	inventoryStatusText,
	liquidSellingPlanId,
}: ResolveVariantStateArgs): ResolvedVariantState {
	const sellingPlanId = normalizeSellingPlanId(liquidSellingPlanId)
	const liquidInventoryStatus = deriveLiquidInventoryStatus({
		available,
		preorder,
		inventoryStatus,
	})
	const manualOverrideStatus = normalizeInventoryStatus(inventoryStatusOverride)

	if (manualOverrideStatus !== '') {
		return {
			inventoryStatus: manualOverrideStatus,
			available: false,
			preorder: false,
			inventoryStatusText,
			sellingPlanId: '',
			shippingText: '',
		}
	}

	// Fast path: no liquid-provided selling plan and no preorder flag — check STOQ runtime once
	const stoqPlan = sellingPlanId === '' ? getRestockRocketSellingPlanForVariant(variantId) : null
	const resolvedSellingPlanId = sellingPlanId || normalizeSellingPlanId(stoqPlan?.shopify_selling_plan_id)

	if (resolvedSellingPlanId === '' && liquidInventoryStatus !== 'PreOrder') {
		return {
			inventoryStatus: liquidInventoryStatus,
			available,
			preorder,
			inventoryStatusText,
			sellingPlanId: '',
			shippingText: '',
		}
	}

	const numericVariantId = Number(variantId)
	const hasValidVariantId = Number.isFinite(numericVariantId)

	const inventoryPolicy = hasValidVariantId ? getRestockRocketInventoryPolicyForVariant(numericVariantId) : ''
	const inventoryQuantity = hasValidVariantId ? getRestockRocketInventoryQuantityForVariant(numericVariantId) : null
	const stoqSoldOut =
		resolvedSellingPlanId !== '' &&
		inventoryPolicy === 'deny' &&
		inventoryQuantity !== null &&
		inventoryQuantity < 1
	const stoqPreorder =
		resolvedSellingPlanId !== '' &&
		inventoryPolicy === 'continue' &&
		inventoryQuantity !== null &&
		inventoryQuantity < 1
	const stoqPreorderCapReached = stoqPreorder && isStoqPreorderCapReached(variantId)

	// Derive shipping text from the already-fetched plan or a fresh lookup only if needed
	const shippingText = (stoqPreorder || preorder)
		? normalizeOptionalText(stoqPlan?.shipping_text) || getRestockRocketShippingTextForVariant(variantId)
		: ''

	if (stoqSoldOut || stoqPreorderCapReached) {
		return {
			inventoryStatus: 'OutOfStock',
			available: false,
			preorder: false,
			inventoryStatusText: window.variantStrings?.soldOut || inventoryStatusText,
			sellingPlanId: '',
			shippingText: '',
		}
	}

	if (!stoqPreorder) {
		return {
			inventoryStatus: liquidInventoryStatus,
			available: liquidInventoryStatus === 'InStock' || liquidInventoryStatus === 'PreOrder',
			preorder: liquidInventoryStatus === 'PreOrder',
			inventoryStatusText,
			sellingPlanId: resolvedSellingPlanId,
			shippingText,
		}
	}

	return {
		inventoryStatus: 'PreOrder',
		available: true,
		preorder: true,
		inventoryStatusText: window.variantStrings?.preorder || inventoryStatusText,
		sellingPlanId: resolvedSellingPlanId,
		shippingText,
	}
}

export function syncResolvedStoqVariantSelection({
	form,
	submitButton,
	variantId,
	resolvedState,
	fallbackShippingText,
	oosPopupUrl,
	bisProvider = 'klaviyo',
	renderButton = true,
}: SyncVariantSelectionArgs) {
	if (isPurchasableInventoryStatus(resolvedState.inventoryStatus)) {
		submitButton.removeAttribute('data-oos-popup-trigger')
		submitButton.removeAttribute('data-uc-bis-variant-id')
		submitButton.type = 'submit'
	} else if (oosPopupUrl) {
		submitButton.setAttribute(
			'data-uc-bis-variant-id',
			normalizeOptionalText(String(variantId ?? ''))
		)
		if (bisProvider === 'klaviyo') {
			submitButton.setAttribute('data-oos-popup-trigger', oosPopupUrl)
		} else {
			submitButton.removeAttribute('data-oos-popup-trigger')
		}
		submitButton.type = 'button'
	}
	submitButton.dataset.inventoryStatus = resolvedState.inventoryStatus

	syncStoqSellingPlanInput({
		form,
		variantId,
		preorder: resolvedState.preorder,
		liquidSellingPlanId: resolvedState.sellingPlanId,
	})
	syncStoqShippingTextInput({
		form,
		preorder: resolvedState.preorder,
		shippingText:
			resolvedState.shippingText ||
			normalizeOptionalText(fallbackShippingText),
	})

	if (renderButton) {
		const renderRoot =
			submitButton.closest<HTMLElement>('section[id^="MainProduct-"]') ||
			form.closest<HTMLElement>('section[id^="MainProduct-"]')
		renderStoqButtonForVariant(variantId, resolvedState.available, renderRoot)
	}
}

export function applyStoqPreorderProperties({
	formData,
	preorder,
	shippingText,
}: ApplyPreorderPropertiesArgs) {
	const resolvedShippingText = preorder
		? normalizeOptionalText(shippingText) ||
			getRestockRocketShippingTextForVariant(
				formData.get('id') || formData.get('items[0][id]')
			)
		: ''

	setLineItemProperty(formData, PROPERTIES.displayPreorder, resolvedShippingText)
	setLineItemProperty(formData, PROPERTIES.preorder, preorder ? 'true' : '')
	setLineItemProperty(formData, PROPERTIES.shippingText, resolvedShippingText)
}
